import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Modal from '../../components/modal/modal.component';
import {disableSwipeToClose} from "../../utils/tools/modals";
import {
	getNearestClients,
	postConfirmClient,
	getLatestLocations,
	getLocationQR,
	postReadQrCode
} from "services/geolocation.service"
import {DataSelectable} from "@models/common";
import {Geolocation} from "@capacitor/geolocation";
import {AndroidSettings, IOSSettings, NativeSettings} from "capacitor-native-settings";
import {IonAlert, IonLoading, IonToast, isPlatform, useIonViewWillLeave} from "@ionic/react";
import {StyledModalContent} from "@components/modal/modal.style";
import {StyledButton} from "@components/button/button.style";
import {BarcodeScanner} from "@capacitor-community/barcode-scanner";
import {StyledDataTable, StyledEmptyContainer} from "@components/content/content.style";
import moment, {Moment} from "moment/moment";
import {ToastModel} from "types/toast";
import CloseImage from "@assets/images/e-remove.svg";
import {Toast} from "@capacitor/toast";
import CircleImage, {ImageTypes} from "@components/circleImage/circleImage.component";
import {EventType} from "@enums/eventType";
import {useEvent} from "@hooks/useEvent";
import {Profile} from "@models/profile";
import {Preferences} from "@capacitor/preferences";

type LocationModalProps = {
	isOpen: boolean;
	onClose: () => void;
	onSuccess: () => void;
};

export type LocationQrData = {
	user: bigint,
	time: string,
	client: bigint,
	latitude: number,
	longitude: number,
}

export type Location = {
	start_latitude: string,
	start_longitude: string,
	type: string,
	valid_to: string,
	client: DataSelectable,
	is_expiring: boolean,
	start_at: string,
	end_at_for_list: string,
	end_at: string,
	auto_end_at: string,
}

const LocationModal: React.FC<LocationModalProps> = (props: LocationModalProps) => {

	const {t} = useTranslation();
	const [clients, setClients] = useState<DataSelectable[]>([]);
	const [latestLocations, setLatestLocations] = useState<Location[]>([]);
	const [qrImage, setQrImage] = useState<string>();
	const [showPermissionAlert, setShowPermissionAlert] = useState<boolean>(false);
	const [showClientsModal, setShowClientsModal] = useState<boolean>(false);
	const [clientAction, setClientAction] = useState<string>('');
	const [showScanQrModal, setShowScanQrModal] = useState<boolean>(false);
	const [showLocationModal, setShowLocationModal] = useState<boolean>(false);
	const [showLoading, setShowLoading] = useState(false);
	const [toast, setToast] = useState<ToastModel>();
	const [scanStartAt, setScanStartAt] = useState<Moment|undefined>();
	const [scanIntervalAt, setScanIntervalAt] = useState<Moment|undefined>();
	const [submitted, setSubmitted] = useState(false);
	const [profile, setProfile] = useState<Profile | undefined>();

	const {dispatch} = useEvent();

	const successHandledRef = useRef<boolean>(false);

	const handleClose = () => {
		props.onClose();
		if (!successHandledRef.current) {
			stopScan();
		}
	};

	const handleOnSuccess = async () => {
		successHandledRef.current = true;
		dispatch(EventType['REPORT.LIST.LOCATION'])
		await stopScan();
		props.onSuccess();
	};

	useIonViewWillLeave(() => {
		if (document.body.classList.contains('scanner-active')) {
			stopScan();
		}
	});

	const handleOpen = () => {
		setShowLocationModal(props.isOpen);
		fetchRecentLocations();
	};

	useEffect(() => {
		handleOpen();
	}, [props.isOpen]);

	const showClientsList = async (action: string) => {
		await getClientsForCurrentLocation();
		setClientAction(action);
	}

	const fetchRecentLocations = async () => {
		await getLatestLocations()
			.then((response) => {
				setLatestLocations(response.data);
			})
	}

	const getClientsForCurrentLocation = async () => {
		let hasPermissions = await Geolocation.checkPermissions()
			.then((location) => {
				return location.location;
			})
			.catch(reason => {
				return 'error';
			});
		setShowLoading(true)
		if (hasPermissions === 'denied' || hasPermissions === 'error') {
			setShowPermissionAlert(true);
		} else {
			await getNearestClients()
				.then((response) => {
					setClients(response.data);
					setShowClientsModal(true);
				})
				.catch(reason => {

				})
		}
		setShowLoading(false)
	}

	const confirmClientSelection = async (client: DataSelectable) => {
		if (!submitted){
			let hasPermissions = await Geolocation.checkPermissions()
				.then((location) => {
					return location.location;
				})
				.catch(reason => {
					return 'error';
				});
			if (hasPermissions === 'denied' || hasPermissions === 'error') {
				setShowPermissionAlert(true);
				setSubmitted(false);
			} else {
				await postConfirmClient(client.id)
					.then(async (response) => {
						setToast({
							message: t('locationModal.gpsLocation.success'),
							type: 'success'
						});

						await handleOnSuccess();
					})
					.catch(reason => {
						setToast({
							message: t('locationModal.gpsLocation.error'),
							type: 'danger'
						});
					})
				setShowClientsModal(false);
				setSubmitted(false);
			}
		}

	}

	const getQrImage = async (client: DataSelectable) => {
		await getLocationQR(client.id)
			.then((response) => {
				setQrImage(response.data)
			})
			.catch(reason => {
				setToast({
					message: t('locationModal.showQrCode.error'),
					type: 'danger'
				});
			})
	}

	let scanTimeout: NodeJS.Timeout | undefined;
	useEffect(() => {
		checkScanInterval();
		if (scanTimeout) clearInterval(scanTimeout);

		if (scanStartAt) {
			scanTimeout = setInterval(checkScanInterval, 1000);
		}

		return () => {
			if (scanTimeout) clearInterval(scanTimeout);
		};
	}, [scanIntervalAt])

	const checkScanInterval = () => {
		let now = moment();
		if (scanStartAt && now.diff(scanStartAt, 's') > 15) {
			stopScan();
		}
	}

	const startScanQrImage = async () => {
		const hasCameraPermissions = await BarcodeScanner.checkPermission({ force: true });
		if (hasCameraPermissions.denied) {
			setShowPermissionAlert(true);
		} else {
			await Toast.show({
				text: t('locationModal.otherQrLocation.scanningLabel'),
				duration: 'short',
				position: 'center',
			});

			await Toast.show({
				text: t('locationModal.otherQrLocation.swipeDownToClose'),
				duration: 'short',
				position: 'center',
			});

			scanQrImage()
			setScanStartAt(moment());
			setScanIntervalAt(moment());
		}
	}

	const scanQrImage = async () => {
		let scanStarted:any = undefined;

		document.body.classList.add('scanner-active');
		await BarcodeScanner.hideBackground(); // make background of WebView transparent

		await BarcodeScanner.startScanning({
		}, (value) => {
			let now = moment();
			if (scanStarted === undefined) {
				scanStarted = now;
			}

			if (value.hasContent && value.content) {
				onReadQrCode(value.content);
				stopScan();
			}

			if (now.diff(scanStarted, 's') > 15) {
				stopScan();
			}
		});

		setTimeout(() => {
			BarcodeScanner.enableTorch();
		}, 1000);
	};

	const stopScan = async () => {
		await BarcodeScanner.showBackground();
		await BarcodeScanner.stopScan();
		await BarcodeScanner.disableTorch();

		document.body.classList.remove('scanner-active');

		setScanStartAt(undefined)
		setScanIntervalAt(undefined)
		if (scanTimeout) clearInterval(scanTimeout);
	};

	const onReadQrCode = async (json: string) => {
		try {
			const data = JSON.parse(json) as LocationQrData;

			await postReadQrCode(data)
				.then(async (response) => {
					setToast({
						message: t('locationModal.otherQrLocation.success'),
						type: 'success'
					});

					await handleOnSuccess();
				})
				.catch(reason => {
					setToast({
						message: t('locationModal.otherQrLocation.error'),
						type: 'danger'
					});
				})
		} catch (error) {
			setToast({
				message: t('locationModal.otherQrLocation.error'),
				type: 'danger'
			});
		}
	}

	const getProfileData = async () => {
		let profileId = await Preferences.get({'key': 'profile_id'});
		let profileType = await Preferences.get({'key': 'profile_type'});
		let profileCompanyName = await Preferences.get({'key': 'profile_company_name'});

		if (profileId.value !== null && profileType.value !== null && profileCompanyName.value !== null) {
			setProfile({
				id: parseInt(profileId.value),
				type: profileType.value,
				company: {
					name: profileCompanyName.value
				}
			})
		}
	};

	useEffect(() => {
		getProfileData();
	}, []);

	return (
		<>
			<Modal
				isOpen={showLocationModal}
				canDismiss={disableSwipeToClose}
				maxHeight={'50%'}
				disableClose={true}
				onDidDismiss={handleClose}>

				<StyledModalContent>
					<img src={CloseImage} className="close" onClick={() => handleClose()}/>
					<h1 className="title">{t("locationModal.title")}</h1>
					<div className="content full-height">
						{!profile && <StyledButton type="button" onClick={() => {
							showClientsList('gps')
						}}>{t("locationModal.gpsLocation.button")}</StyledButton>}
						{!profile && (isPlatform('ios') || isPlatform('android')) && <StyledButton type="button"
									  onClick={startScanQrImage}>{t("locationModal.otherQrLocation.button")}</StyledButton>}
						<StyledButton type="button" onClick={() => {
							showClientsList('qr')
						}}>{t("locationModal.showQrCode.button")}</StyledButton>

						{latestLocations && latestLocations.length > 0 &&
							<>
								<h5>{t("locationModal.latest.title")}</h5>
								<StyledDataTable className="no-margin">
									{latestLocations.map((data, i) => <div key={i} className="data-row">
										<div className="data-icon-title">
											{moment(data.start_at).format('YYYY-MM-DD H:mm:ss')} {data.end_at_for_list ? '- ' + data.end_at_for_list : ''} ({data.type}), {data.client.name}
										</div>
									</div>)
									}
								</StyledDataTable>
							</>
						}
					</div>
				</StyledModalContent>
			</Modal>

			{showClientsModal &&
				<Modal
					isOpen={true}
					canDismiss={disableSwipeToClose}
					maxHeight={'75%'}
					disableClose={true}
					onDidDismiss={() => setShowClientsModal(false)}>

					<StyledModalContent className="scrollable">
						<img src={CloseImage} className="close" onClick={() => setShowClientsModal(false)}/>

						<h1 className="title">{t("locationModal.clients.title")}</h1>
						<div className="content full-height">
							<StyledDataTable className="no-margin">
								{clients && clients.map((data, i) => <div key={i} className="data-row"
																		  onClick={async () => {
																			  setSubmitted(true);
																			  if (clientAction == 'gps') {
																				  await confirmClientSelection(data);
																			  } else if (clientAction == 'qr') {
																				  await getQrImage(data);
																			  }

																			  setClientAction('');
																			  setShowClientsModal(false);
																		  }}>
									<div className="data-icon-title">{data.name}</div>
								</div>)
								}
								{clients && clients.length === 0 &&
									<StyledEmptyContainer className="margin-top">
										<CircleImage image={ImageTypes.BRIEF_CASE} color="grey"/>
										<h3>{t('locationModal.clients.empty')}</h3>
									</StyledEmptyContainer>
								}
							</StyledDataTable>
						</div>
					</StyledModalContent>
				</Modal>
			}

			{qrImage &&
				<Modal
					isOpen={true}
					canDismiss={disableSwipeToClose}
					maxHeight={'75%'}
					disableClose={true}
					onDidDismiss={() => setQrImage(undefined)}>

					<StyledModalContent>
						<img src={CloseImage} className="close" onClick={() => setQrImage(undefined)}/>

						<h1 className="title">{t("locationModal.showQrCode.title")}</h1>
						<div className="content full-height center">
							<img src={qrImage}/>
						</div>
					</StyledModalContent>
				</Modal>
			}

			<IonAlert
				isOpen={showPermissionAlert}
				onDidDismiss={() => setShowPermissionAlert(false)}
				header={t('common.geolocation_permissions.alert')}
				buttons={[
					{
						text: t('common.alertCancel'),
						role: 'cancel',
						cssClass: 'secondary',
						handler: () => {
							setShowPermissionAlert(false);
						},
					},
					{
						text: t('common.geolocation_permissions.goToAppSettings'),
						handler: async () => {
							setShowPermissionAlert(false);
							await NativeSettings.open({
								optionAndroid: AndroidSettings.ApplicationDetails,
								optionIOS: IOSSettings.App
							})
						}
					}
				]}
			/>

			<IonLoading onDidDismiss={() => setShowLoading(false)} isOpen={showLoading}/>

			<IonToast
				isOpen={!!toast}
				onDidDismiss={() => setToast(undefined)}
				message={toast?.message}
				duration={toast?.duration ? toast.duration : 6000}
				position="top"
				color={toast?.type}
			/>
		</>
	);
};

export default LocationModal;
